import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../config/axiosInstance";
import { SEARCH } from "../../config/apiConfig";
// Tema escuro dos campos/editores/modais do flowbite (global; este componente vive no Sidebar).
import "../../styles/dark-inputs.css";
import {
  HiOutlineClipboardDocumentList,
  HiOutlinePencilSquare,
  HiOutlineSquares2X2,
  HiOutlineUserCircle,
  HiXMark,
} from "react-icons/hi2";
import { HiSearch } from "react-icons/hi";

const SEARCH_OPEN_EVENT = "global-search:open";
const MIN_QUERY_LENGTH = 2;
const LOADING_ROW_COUNT = 4;

const EMPTY_RESULTS = {
  query: "",
  leads: [],
  cards: [],
  tasks: [],
  notes: [],
  totalCount: 0,
};

const TYPE_META = {
  leads: {
    label: "Contato",
    sectionLabel: "Contatos",
    icon: HiOutlineUserCircle,
    accent: "bg-sky-50 text-sky-600 border-sky-100",
  },
  cards: {
    label: "Card",
    sectionLabel: "Cards",
    icon: HiOutlineSquares2X2,
    accent: "bg-orange-50 text-orange-600 border-orange-100",
  },
  tasks: {
    label: "Tarefa",
    sectionLabel: "Tarefas",
    icon: HiOutlineClipboardDocumentList,
    accent: "bg-violet-50 text-violet-600 border-violet-100",
  },
  notes: {
    label: "Nota",
    sectionLabel: "Notas",
    icon: HiOutlinePencilSquare,
    accent: "bg-emerald-50 text-emerald-600 border-emerald-100",
  },
};

const isEditableTarget = (target) => {
  if (!target || typeof target !== "object") return false;
  const tagName = String(target.tagName || "").toUpperCase();
  return (
    target.isContentEditable ||
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT"
  );
};

const normalizeResultGroups = (payload = EMPTY_RESULTS) => ({
  leads: Array.isArray(payload.leads) ? payload.leads : [],
  cards: Array.isArray(payload.cards) ? payload.cards : [],
  tasks: Array.isArray(payload.tasks) ? payload.tasks : [],
  notes: Array.isArray(payload.notes) ? payload.notes : [],
  totalCount: Number(payload.totalCount || 0) || 0,
  query: String(payload.query || ""),
});

const flattenGroups = (groups) =>
  ["leads", "cards", "tasks", "notes"].flatMap((groupKey) =>
    (groups[groupKey] || []).map((item, index) => ({
      ...item,
      groupKey,
      groupLabel: TYPE_META[groupKey]?.label || groupKey,
      groupIndex: index,
      flatLabel: `${groupKey}:${item?.id || index}`,
    })),
  );

const buildItemRoute = (item) => {
  if (!item) return null;

  if (
    (item.groupKey === "tasks" || item.type === "task") &&
    item.cardId &&
    item.taskId
  ) {
    const baseRoute = `/crm?cardId=${encodeURIComponent(String(item.cardId))}`;
    const separator = baseRoute.includes("?") ? "&" : "?";
    return `${baseRoute}${separator}highlight=${encodeURIComponent(
      `task:${String(item.taskId)}`,
    )}`;
  }

  return item.route || null;
};

const GlobalSearchPalette = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const activeItemRef = useRef(null);
  const requestCounterRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState(normalizeResultGroups());
  const [activeIndex, setActiveIndex] = useState(0);

  const openPalette = useCallback(() => {
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery("");
    setGroups(normalizeResultGroups());
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    const onOpen = () => openPalette();
    window.addEventListener(SEARCH_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SEARCH_OPEN_EVENT, onOpen);
  }, [openPalette]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const isEditable = isEditableTarget(event.target);
      const key = String(event.key || "");

      if (
        !open &&
        !isEditable &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        key === "/"
      ) {
        event.preventDefault();
        openPalette();
        return;
      }

      if (
        !open &&
        !isEditable &&
        (event.metaKey || event.ctrlKey) &&
        key.toLowerCase() === "k"
      ) {
        event.preventDefault();
        openPalette();
        return;
      }

      if (!open) return;

      const flat = flattenGroups(groups);
      if (key === "Escape") {
        event.preventDefault();
        closePalette();
        return;
      }

      if (key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, Math.max(flat.length - 1, 0)));
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      if (key === "Enter") {
        event.preventDefault();
        const item = flat[activeIndex] || flat[0];
        const route = buildItemRoute(item);
        closePalette();
        if (route) navigate(route);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, closePalette, groups, navigate, open, openPalette]);

  useEffect(() => {
    if (!open) return;
    const onRouteChange = () => closePalette();
    window.addEventListener("popstate", onRouteChange);
    return () => window.removeEventListener("popstate", onRouteChange);
  }, [closePalette, open]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(async () => {
      const normalized = String(query || "").trim();
      if (normalized.length < MIN_QUERY_LENGTH) {
        setGroups(normalizeResultGroups());
        setLoading(false);
        setActiveIndex(0);
        return;
      }

      const requestId = ++requestCounterRef.current;
      setLoading(true);

      try {
        const { data } = await axios.get(SEARCH.GLOBAL, {
          params: {
            q: normalized,
            limitPerGroup: 5,
          },
        });
        if (requestId !== requestCounterRef.current) return;
        setGroups(normalizeResultGroups(data?.data || data || EMPTY_RESULTS));
        setActiveIndex(0);
      } catch (error) {
        if (requestId !== requestCounterRef.current) return;
        setGroups(normalizeResultGroups());
      } finally {
        if (requestId === requestCounterRef.current) {
          setLoading(false);
        }
      }
    }, 220);

    return () => window.clearTimeout(timer);
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus?.();
    inputRef.current?.select?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex((prev) => {
      const flat = flattenGroups(groups);
      if (!flat.length) return 0;
      return Math.min(prev, flat.length - 1);
    });
  }, [groups, open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => closePalette();
    window.addEventListener("global-search:close", handler);
    return () => window.removeEventListener("global-search:close", handler);
  }, [closePalette, open]);

  useEffect(() => {
    closePalette();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const flatResults = useMemo(() => flattenGroups(groups), [groups]);
  const hasActiveQuery = String(query || "").trim().length >= MIN_QUERY_LENGTH;
  const showLoadingState = loading && hasActiveQuery;

  const loadingRows = useMemo(
    () =>
      Array.from({ length: LOADING_ROW_COUNT }, (_, index) => ({
        id: `loading-${index}`,
      })),
    [],
  );

  function handleSelect(item) {
    const route = buildItemRoute(item);
    // Fecha SEMPRE ao selecionar — mesmo sem rota resolvível — e só então
    // navega. Fechar antes do navigate evita o painel ficar preso quando a
    // rota destino tem o mesmo pathname atual (o efeito de fechar por
    // location.pathname não dispara nesse caso).
    closePalette();
    if (route) navigate(route);
  }

  // Sem AnimatePresence/exit: o painel vive dentro do Sidebar, que é desmontado
  // pelo Suspense de rota ao navegar (ex.: clicar num card carrega o chunk do
  // CRM). Uma animação de saída deixaria o backdrop (portal em document.body,
  // z-[10000]) órfão no DOM, capturando todos os cliques e travando a tela.
  // Render condicional garante remoção imediata em open=false ou no desmonte.
  if (typeof document === "undefined" || !open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000]">
      <motion.div
        className="dark-backdrop absolute inset-0 bg-slate-950/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onMouseDown={closePalette}
      >
        <motion.div
          className="mx-auto mt-8 w-[min(100%-1.5rem,44rem)]"
          initial={{ opacity: 0, y: -12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.16 }}
          onMouseDown={(event) => event.stopPropagation()}
        >
            <div className="dark-panel overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_32px_80px_rgba(15,23,42,0.24)]">
              <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
                <HiSearch className="h-5 w-5 shrink-0 text-slate-400" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="O que você procura?"
                  className="w-full border-0 bg-transparent p-0 text-[15px] font-medium text-slate-900 outline-none ring-0 placeholder:text-slate-400"
                />
                <div className="flex items-center gap-2">
                  {showLoadingState ? (
                    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-500">
                      <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                      Buscando
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={closePalette}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Fechar busca"
                  >
                    <HiXMark className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="max-h-[65vh] overflow-y-auto px-2 py-2">
                {!hasActiveQuery ? (
                  <div className="px-3 py-8" />
                ) : showLoadingState ? (
                  <div className="space-y-2 px-1 py-2" role="status" aria-live="polite">
                    {loadingRows.map((row) => (
                      <div
                        key={row.id}
                        className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-3"
                      >
                        <div className="h-9 w-9 shrink-0 rounded-xl bg-slate-200/80 animate-pulse" />
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="h-4 w-3/5 rounded-full bg-slate-200/80 animate-pulse" />
                          <div className="h-3 w-4/5 rounded-full bg-slate-100 animate-pulse" />
                        </div>
                        <div className="h-6 w-16 shrink-0 rounded-full bg-slate-100 animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : flatResults.length ? (
                  ["leads", "cards", "tasks", "notes"].map((groupKey) => {
                    const items = groups[groupKey] || [];
                    if (!items.length) return null;
                    const meta = TYPE_META[groupKey] || {};
                    const Icon = meta.icon || HiSearch;
                    return (
                      <div key={groupKey} className="mb-2">
                        <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                          <Icon className="h-4 w-4" />
                          <span>{meta.sectionLabel || meta.label || groupKey}</span>
                          <span className="text-slate-300">
                            {items.length.toString().padStart(2, "0")}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {items.map((item) => {
                            const globalIndex = flatResults.findIndex(
                              (candidate) =>
                                candidate.type === item.type &&
                                String(candidate.id) === String(item.id) &&
                                candidate.route === item.route,
                            );
                            const selected = globalIndex === activeIndex;
                            const RowIcon = TYPE_META[item.groupKey || groupKey]?.icon || Icon;
                            return (
                              <button
                                key={`${groupKey}:${String(item.id)}`}
                                ref={selected ? activeItemRef : null}
                                type="button"
                                onMouseEnter={() => setActiveIndex(globalIndex)}
                                onClick={() => handleSelect(item)}
                                className={[
                                  "flex w-full items-start gap-3 rounded-2xl border px-3 py-2 text-left transition",
                                  selected
                                    ? "border-orange-200 bg-orange-50 shadow-sm"
                                    : "border-transparent hover:border-slate-200 hover:bg-slate-50",
                                ].join(" ")}
                              >
                                <span
                                  className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${TYPE_META[item.groupKey || groupKey]?.accent || "bg-slate-50 text-slate-500 border-slate-100"}`}
                                >
                                  <RowIcon className="h-4.5 w-4.5" />
                                </span>
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm font-semibold text-slate-900">
                                    {item.title || "—"}
                                  </span>
                                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                                    {item.subtitle || item.typeLabel || ""}
                                  </span>
                                </span>
                                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                                  {meta.label || item.typeLabel || item.type}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-3 py-8 text-sm text-slate-400">
                    Nenhum resultado.
                  </div>
                )}
              </div>

            </div>
          </motion.div>
        </motion.div>
    </div>,
    document.body,
  );
};

export default GlobalSearchPalette;
